import React, { useEffect, useState } from 'react';

import { css } from '@emotion/react';
import { Box, Link, Text } from '@opendoor/bricks-next';
import { motion } from 'framer-motion';

import type { MegaNavItem } from '../types';

interface MegaNavFlyoutProps {
  item: MegaNavItem;
  isVisible: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  topOffset: number;
}

const flyoutVariants = {
  hidden: {
    opacity: 0,
    y: -10,
    transition: {
      duration: 0.2,
      ease: 'easeOut',
    },
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.2,
      ease: 'easeOut',
    },
  },
};

const MegaNavFlyout: React.FC<MegaNavFlyoutProps> = ({
  item,
  isVisible,
  onMouseEnter,
  onMouseLeave,
  topOffset,
}) => {
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);

  // Reset animation complete state when visibility changes
  useEffect(() => {
    if (!isVisible) {
      setIsAnimationComplete(false);
    }
  }, [isVisible]);

  const handleAnimationComplete = (definition: string) => {
    if (definition === 'visible') {
      setIsAnimationComplete(true);
    }
  };

  // Only enable mouse events after the open animation completes
  const enableMouseEvents = isVisible && isAnimationComplete;

  return (
    <motion.div
      initial="hidden"
      animate={isVisible ? 'visible' : 'hidden'}
      variants={flyoutVariants}
      onAnimationComplete={handleAnimationComplete}
      onMouseEnter={enableMouseEvents ? onMouseEnter : undefined}
      onMouseLeave={enableMouseEvents ? onMouseLeave : undefined}
      css={css`
        position: fixed;
        top: ${topOffset}px;
        left: 0;
        width: 100vw;
        background: white;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        z-index: 1000;
        padding: 32px 0;
        /* Always render in HTML for SEO, but control visibility with CSS */
        opacity: ${isVisible ? 1 : 0};
        visibility: ${isVisible ? 'visible' : 'hidden'};
        pointer-events: ${isVisible ? 'auto' : 'none'};
      `}
    >
      <Box width="100%" paddingHorizontal="$11x">
        <Box display="flex" flexDirection="row" gap={180} justifyContent="center">
          {item.sections?.map((section, sectionIndex) => (
            <Box key={sectionIndex}>
              <Text tag="p" typography="$bodyMedium" fontWeight="$medium" marginBottom="$4x">
                {section.title}
              </Text>
              <Box display="flex" flexDirection="column" gap="$3x">
                {section.links.map((link, linkIndex) => (
                  <Link
                    key={linkIndex}
                    href={link.href}
                    size="medium"
                    variant="secondary"
                    textDecorationLine="none"
                    aria-label={`Navigate to ${link.name}`}
                    analyticsName={`cosmos-mega-nav-${item.name.toLowerCase()}-${link.name
                      .toLowerCase()
                      .replace(/\s+/g, '-')}`}
                  >
                    <Text tag="span" typography="$bodyMedium">
                      {link.name}
                    </Text>
                  </Link>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </motion.div>
  );
};

export default MegaNavFlyout;
